import React, { useState } from 'react';
import { Spin } from 'antd';
import { RequestTrackingResponse } from '@openapi';
import { useTimeRange, TimeRange } from './hooks/useTimeRange';
import { useStatistics } from './hooks/useStatistics';
import { useRequestsData } from './hooks/useRequestsData';
import StatisticsCards from './components/StatisticsCards';
import ChartsSection from './components/ChartsSection';
import ApiVersionDetails from './components/ApiVersionDetails';
import RequestsTable from './components/RequestsTable';
import DeviceSearch from './components/DeviceSearch';
import RequestDetailModal from './components/RequestDetailModal';
import DashboardHeader from './components/DashboardHeader';
import TimeRangeSelector from './components/TimeRangeSelector';
import './RequestTrackingDashboard.scss';

const RequestTrackingDashboard: React.FC = () => {
  const { timeRange, setTimeRange, customDateRange, setCustomDateRange } = useTimeRange();
  const [requestViewMode, setRequestViewMode] = useState<'slowest' | 'errors'>('errors');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [detailModalVisible, setDetailModalVisible] = useState<boolean>(false);
  const [selectedRequest, setSelectedRequest] = useState<RequestTrackingResponse | null>(null);

  const {
    statistics,
    errorDistribution,
    loading: statsLoading,
    refetch: refetchStats,
  } = useStatistics(timeRange, customDateRange);

  const {
    requests,
    total,
    loading: requestsLoading,
    refetch: refetchRequests,
  } = useRequestsData(timeRange, customDateRange, requestViewMode, currentPage, pageSize);

  const handleRefresh = () => {
    refetchStats();
    refetchRequests();
  };

  const handleTimeRangeChange = (value: TimeRange) => {
    setTimeRange(value);
    if (value !== 'custom') {
      setCustomDateRange(null);
      setCurrentPage(1);
    }
  };

  const showRequestDetail = (request: RequestTrackingResponse) => {
    setSelectedRequest(request);
    setDetailModalVisible(true);
  };

  const handleDetailModalClose = () => {
    setDetailModalVisible(false);
    setSelectedRequest(null);
  };

  const handleViewModeChange = (mode: 'slowest' | 'errors') => {
    setRequestViewMode(mode);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number, newPageSize: number) => {
    setCurrentPage(page);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    }
  };

  return (
    <div className="request-tracking-dashboard">
      <DashboardHeader onRefresh={handleRefresh} loading={statsLoading || requestsLoading} />

      <TimeRangeSelector
        timeRange={timeRange}
        customDateRange={customDateRange}
        onTimeRangeChange={handleTimeRangeChange}
        onCustomDateRangeChange={setCustomDateRange}
      />

      <Spin spinning={statsLoading} tip="Loading statistics...">
        <div style={{ minHeight: 200 }}>
          {statistics && (
            <>
              <StatisticsCards statistics={statistics} />
              <ChartsSection statistics={statistics} errorDistribution={errorDistribution} />
              <ApiVersionDetails statistics={statistics} />
            </>
          )}
        </div>
      </Spin>

      <RequestsTable
        viewMode={requestViewMode}
        onViewModeChange={handleViewModeChange}
        requests={requests}
        loading={requestsLoading}
        currentPage={currentPage}
        pageSize={pageSize}
        total={total}
        onPageChange={handlePageChange}
        onShowDetail={showRequestDetail}
      />

      <DeviceSearch onShowDetail={showRequestDetail} />

      <RequestDetailModal visible={detailModalVisible} request={selectedRequest} onClose={handleDetailModalClose} />
    </div>
  );
};

export default RequestTrackingDashboard;
